import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, ExternalLink, Cookie } from "lucide-react";
import { openCookieSettings } from "@/lib/cookieConsent";

export default function Footer({ company }) {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-[#12151A] text-white/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <span className="font-display font-extrabold text-xl text-[#E11D2A]">
                SIM<span className="text-[#E11D2A]">SERV</span>
              </span>
            </div>
            <p className="text-sm leading-relaxed max-w-md">
              {company.name} — service auto complet în Slobozia: mecanică,
              tinichigerie, vopsitorie, ITP și diagnoză.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <a
                href="https://maps.app.goo.gl/Fxo6y7PrWLrTMuwq9"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-google-maps-link"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-[#FF5A1F] text-white/80 hover:text-white text-sm font-semibold transition-colors duration-200"
              >
                <MapPin className="w-4 h-4" /> Găsește-ne pe Google Maps
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-display font-bold text-white mb-4">Navigare</h4>
            <ul className="space-y-2.5 text-sm">
              {[
                { href: "#servicii", label: "Servicii" },
                { href: "#despre", label: "Despre Noi" },
                { href: "#contact", label: "Contact" },
              ].map((l) => (
                <li key={l.href}>
                  <a href={l.href} className="hover:text-[#FF5A1F] transition-colors duration-200">
                    {l.label}
                  </a>
                </li>
              ))}
              <li>
                <Link to="/admin/login" className="hover:text-[#FF5A1F] transition-colors duration-200">
                  Panou Admin
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-display font-bold text-white mb-4">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-[#FF5A1F] shrink-0 mt-0.5" />
                {company.address}
              </li>
              <li className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 text-[#FF5A1F] shrink-0" />
                <span className="font-mono">{company.phone}</span>
              </li>
              {company.email ? (
                <li className="flex items-center gap-2.5">
                  <Mail className="w-4 h-4 text-[#FF5A1F] shrink-0" />
                  {company.email}
                </li>
              ) : null}
              <li className="flex items-center gap-2.5">
                <Clock className="w-4 h-4 text-[#FF5A1F] shrink-0" />
                {company.program_weekdays}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10">
          <p className="text-xs font-bold uppercase tracking-wider text-[#FF5A1F] mb-3">
            Legal — Protecția Consumatorului
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <a
              href="https://reclamatiisal.anpc.ro"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-anpc-sal"
              title="Soluționarea Alternativă a Litigiilor — ANPC"
              className="inline-flex items-stretch bg-white rounded-md overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
            >
              <span className="flex items-center gap-2 px-3 py-2 border-r border-slate-200">
                <span className="flex h-6 w-6 rounded-sm overflow-hidden shrink-0 border border-slate-200">
                  <span className="w-1/3 bg-[#002B7F]" />
                  <span className="w-1/3 bg-[#FCD116]" />
                  <span className="w-1/3 bg-[#CE1126]" />
                </span>
                <span className="font-extrabold text-[#002B7F] text-sm tracking-tight">ANPC</span>
              </span>
              <span className="flex flex-col justify-center px-3 py-1.5">
                <span className="text-[11px] font-bold text-[#1D4ED8] leading-tight uppercase">Soluționarea Alternativă</span>
                <span className="text-[11px] font-bold text-[#1D4ED8] leading-tight uppercase">a Litigiilor (SAL)</span>
              </span>
              <span className="flex items-center px-3 bg-[#1D4ED8] text-white text-[10px] font-bold uppercase tracking-wider">
                Detalii
              </span>
            </a>
            <a
              href="https://www.anpc.ro"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-anpc-site"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/70 hover:text-[#FF5A1F] transition-colors duration-200"
            >
              <ExternalLink className="w-3.5 h-3.5" /> www.anpc.ro
            </a>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/40">
          <p>© {year} {company.name}. Toate drepturile rezervate.</p>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Link
              to="/termeni"
              data-testid="footer-terms-link"
              className="hover:text-[#FF5A1F] transition-colors duration-200"
            >
              Termeni și Condiții
            </Link>
            <button
              type="button"
              onClick={openCookieSettings}
              data-testid="footer-cookie-settings"
              className="inline-flex items-center gap-1.5 hover:text-[#FF5A1F] transition-colors duration-200"
            >
              <Cookie className="w-3.5 h-3.5" /> Setări cookie-uri
            </button>
            <p className="font-mono">Service Auto · Slobozia, Ialomița</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
